import type { CSSProperties, FC, MouseEvent, Ref } from "react";

import { Icon } from "../Icon";
import { Flex } from "../Flex";
import { Text } from "../Text";

import { askAIButtonStyle } from "./AskAIButton.css";
import type { ClassNameProp } from "../../props";
import { mergeClasses } from "@huub-web/utils";

export type AskAIButtonProps = {
  as?: "button" | "div";
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  style?: CSSProperties;
  disabled?: boolean;
  ref?: Ref<HTMLButtonElement>;
  /**
   * Accessible name for the control. The visible label is only "Vragen?", so a
   * descriptive default that conveys this opens the AI assistant is provided
   * (SMA-1679, accessibility) and can be overridden per consumer.
   */
  "aria-label"?: string;
} & ClassNameProp;

export const AskAIButton: FC<AskAIButtonProps> = ({
  as = "button",
  onClick,
  disabled,
  className,
  ref,
  "aria-label": ariaLabel = "Vraag de AI-assistent",
  ...props
}) => {
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    onClick?.(e);
  };

  const isButton = as === "button";

  return (
    <Flex
      ref={ref}
      as={as}
      onClick={handleClick}
      className={mergeClasses(askAIButtonStyle, className)}
      gap="xxs"
      align="center"
      aria-label={ariaLabel}
      {...(isButton
        ? { disabled, type: "button" as const }
        : {
            "data-disabled": disabled || undefined,
            "aria-disabled": disabled,
          })}
      {...props}
    >
      <Text variant="bodyXS" bold>
        Vragen?
      </Text>
      <Icon icon="aiFilled" color="gradient" size="s2" />
    </Flex>
  );
};
